import {createSlice} from "@reduxjs/toolkit";
import i18n from "../i18n";
import {localesNormalizedEqual} from "../constants/locales";
import {fetchUserSettings} from "./userSettingsSlice";


const initialState = {
    authorized: false,
    email: null,
    username: null,
    locale: null
}

const securitySlice = createSlice({
    name: 'security',
    initialState: initialState,
    reducers: {
        loginSuccess(state, action) {
            return {
                ...state,
                authorized: true,
                email: action.payload.email,
                username: action.payload.email,
                locale: action.payload.locale || null
            }
        },
        setLoginData(state, action) {
            const userDataJson = action.payload;
            return {
                ...state,
                authorized: (!!userDataJson.email),
                email: userDataJson.email,
                username: userDataJson.email,
                locale: userDataJson.locale || null
            }
        },
        logout(state, action) {
            return {authorized: false};
        }
    }
})

export const {loginSuccess, setLoginData, logout} = securitySlice.actions
export default securitySlice.reducer


export const fetchCurrentUser = () => async (dispatch, getState) => {
    fetch('/api/user')
        .then((response) => {
            if (response.ok) {
                response.json().then(json => {
                    if (json?.locale && !localesNormalizedEqual(i18n.language, json.locale)) {
                        i18n.changeLanguage(json.locale);
                    }
                    dispatch(setLoginData(json));
                    if (json?.email) {
                        dispatch(fetchUserSettings());
                    }
                    if (json?.email && !json?.locale && window.location.pathname !== '/select-locale') {
                        window.location.replace('/select-locale');
                    }
                    if (json?.email && json?.locale && window.location.pathname === '/select-locale') {
                        window.location.replace('/');
                    }
                });
            }
        })
        .catch((error) => {
            console.log(error);
        });
}

export const fetchLogout = () => async (dispatch, getState) => {
    if(!getState().securitySlice.authorized) {
        return;
    }
    clearRememberMeCookies();
    // fetch('/api/security/logout')
    fetch('/api/logout')
        .then((response) => {
            if (response.ok) {
                dispatch(logout());
                window.location.replace('/signin');
            }
        })
        .catch((error) => {
            console.log(error);
        });
}

/** Cookie names for remember-me token/series returned in LoginRs */
const REMEMBER_TOKEN_COOKIE = 'efics-remember-token';
const REMEMBER_SERIES_COOKIE = 'efics-remember-series';
const REMEMBER_EMAIL_COOKIE = 'efics-remember-email';
const REMEMBER_COOKIE_MAX_AGE_DAYS = 365;

function setCookie(name, value, maxAgeDays) {
    const maxAge = maxAgeDays * 24 * 60 * 60;
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

function clearCookie(name) {
    document.cookie = `${encodeURIComponent(name)}=; path=/; max-age=0`;
}

/** Save token and series from LoginRs to cookies when "Remember me" was checked. */
export function saveRememberMeCookies(email, token, series) {
    if (token) setCookie(REMEMBER_TOKEN_COOKIE, token, REMEMBER_COOKIE_MAX_AGE_DAYS);
    if (series) {
        setCookie(REMEMBER_SERIES_COOKIE, series, REMEMBER_COOKIE_MAX_AGE_DAYS);
    } else {
        clearCookie(REMEMBER_SERIES_COOKIE);
    }
    if (email) setCookie(REMEMBER_EMAIL_COOKIE, email, REMEMBER_COOKIE_MAX_AGE_DAYS);
}

/** Clear remember-me cookies (e.g. on logout). */
export function clearRememberMeCookies() {
    clearCookie(REMEMBER_TOKEN_COOKIE);
    clearCookie(REMEMBER_SERIES_COOKIE);
    clearCookie(REMEMBER_EMAIL_COOKIE);
}

/** Read remember-me data from cookies for token-based login. */
export function getRememberMeFromCookies() {
    const get = (name) => {
        const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$1');
        const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
        return match ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : null;
    };
    const token = get(REMEMBER_TOKEN_COOKIE);
    const series = get(REMEMBER_SERIES_COOKIE);
    const email = get(REMEMBER_EMAIL_COOKIE);
    return (token && email) ? { email, token, series } : null;
}

function loginFailureMessage(status) {
    if (status === 'EMAIL_REQUIRED') {
        return i18n.t('auth.login.errors.emailRequired');
    }
    if (status === 'PASSWORD_REQUIRED') {
        return i18n.t('auth.login.errors.passwordRequired');
    }
    return i18n.t('auth.login.errors.invalidCredentials');
}

export const signIn = (email, password, rememberMe, onLoginSuccess, onLoginError, rememberMeCredentials = null) => async (dispatch, getState) => {
    const signInData = { email };
    if (rememberMeCredentials?.token) {
        signInData.token = rememberMeCredentials.token;
        if (rememberMeCredentials?.series) {
            signInData.series = rememberMeCredentials.series;
        }
    } else {
        signInData.password = password;
        if (rememberMe) {
            signInData.savePassword = true;
        }
    }

    const loginUrl = '/api/LoginRq' + (rememberMe && !rememberMeCredentials ? '?remember-me=true' : '');
    fetch(loginUrl, {
        method: 'POST',
        body: JSON.stringify(signInData),
        headers: {
            "Content-Type": "application/json",
        },
        credentials: 'include',
    })
        .then((response) => {
            return response.json().then((body) => {
                if (body.status === 'OK') {
                    if (body.locale && !localesNormalizedEqual(i18n.language, body.locale)) {
                        i18n.changeLanguage(body.locale);
                    }
                    if (rememberMe && body.token) {
                        saveRememberMeCookies(email, body.token, body.series);
                    }
                    dispatch(loginSuccess({ email: email, locale: body.locale || null }));
                    dispatch(fetchUserSettings());
                    onLoginSuccess?.();
                } else {
                    if (rememberMeCredentials?.token) {
                        clearRememberMeCookies();
                    }
                    onLoginError?.(loginFailureMessage(body.status));
                }
            });
        })
        .catch((error) => {
            console.error('[LoginRq] error', error);
            onLoginError?.(i18n.t('auth.login.errors.generic'));
        });
}

export const selectCurrentUser = (state) => {
    return state.securitySlice && state.securitySlice.authorized ? {
        email: state.securitySlice.email,
        username: state.securitySlice.username,
        locale: state.securitySlice.locale,
        authorized: state.securitySlice.authorized
    } : null;
}
